import { useMemo } from 'react';
import type { UserItem } from '@/types/userItem';
import { cn } from '@/lib/utils';
import { INVENTORY_SHORTCUTS, type InventoryShortcutId } from './inventoryHelpers';

interface InventoryShortcutsProps {
  activeId: InventoryShortcutId;
  onSelect: (id: InventoryShortcutId) => void;
  /** Full unfiltered inventory — used to compute per-shortcut counts. */
  allItems: UserItem[];
}

/**
 * Horizontally scrollable chip row above the inventory toolbar.
 * Applies a preset filter across the full inventory (not just the current filter view).
 */
export function InventoryShortcuts({ activeId, onSelect, allItems }: InventoryShortcutsProps) {
  const counts = useMemo(() => {
    const map = new Map<InventoryShortcutId, number>();
    for (const s of INVENTORY_SHORTCUTS) {
      map.set(s.id, allItems.reduce((n, it) => (s.predicate(it) ? n + 1 : n), 0));
    }
    return map;
  }, [allItems]);

  // Hide non-"all" shortcuts that would return zero — no dead chips.
  const visible = INVENTORY_SHORTCUTS.filter((s) => s.id === 'all' || (counts.get(s.id) ?? 0) > 0);
  if (visible.length <= 1) return null;

  return (
    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar px-3 md:px-6 py-2">
      {visible.map((s) => {
        const isActive = s.id === activeId;
        const count = counts.get(s.id) ?? 0;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onSelect(isActive && s.id !== 'all' ? 'all' : s.id)}
            className={cn(
              'inline-flex items-center gap-1.5 shrink-0 h-8 px-3 rounded-full border text-[12px] font-medium whitespace-nowrap transition-colors',
              isActive
                ? 'bg-active-gold/15 border-active-gold/50 text-active-gold'
                : 'bg-white/[0.03] border-white/10 text-muted-foreground hover:text-foreground hover:border-white/20',
            )}
            title={s.label}
          >
            <span>{s.label}</span>
            {s.id !== 'all' && (
              <span className={cn(
                'tabular-nums text-[10px] px-1.5 py-0.5 rounded-full',
                isActive ? 'bg-active-gold/20 text-active-gold' : 'bg-white/[0.06] text-muted-foreground',
              )}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
